import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { ReactComponent as InfoCircleSVG } from "go-component/images/svg/info-circle.svg";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { EmptyListConfigAction } from "go-core/components/EmptyList";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { ReactComponent as LongArrowGreenSVG } from "../../../../../../../../../images/svg/stocktaking/long-arrow-green.svg";
import { ReactComponent as LongArrowRedSVG } from "../../../../../../../../../images/svg/stocktaking/long-arrow-red.svg";
import {
	StocktakingApi,
	StocktakingProductStateApi,
	StocktakingSummaryApi,
	SummaryApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { StocktakingContext, StocktakingMobileActionsContext } from "../../Preview";
import StocktakingSummary from "../StocktakingSummary";

interface ListState {
	resource?: Record<string, any>;
	stocktaking: StocktakingApi;
	onFetchSummary: () => Promise<void>;
	summary: StocktakingSummaryApi | undefined;
}

const listName = "STOCKTAKING_SUMMARY";
const resourceType = "STOCKTAKING_SUMMARY";

const buildConfig = (
	t: TFunction,
	data: Record<string, any>,
	segmentListService: SegmentListService,
	stocktaking: StocktakingApi,
	organization: OrganizationState,
	summaryData: SummaryApi,
	setSummaryData: (value: SummaryApi) => void
) => {
	const drawColumnWithDifference = (valueToCompare: number, valueToDisplay: string | number): JSX.Element => {
		let icon: JSX.Element;

		if (valueToCompare === 0) icon = <></>;

		icon = valueToCompare > 0 ? <LongArrowGreenSVG className="mt-n1" /> : <LongArrowRedSVG />;

		return (
			<div className="d-inline-flex align-items-center">
				<span className="me-1">{valueToDisplay}</span>
				{icon}
			</div>
		);
	};

	let config = {
		fields: [
			{
				id: "product",
				name: t("common.word.product"),
				render: (item: StocktakingProductStateApi) => (
					<Link
						rel={"noreferrer"}
						target={"_blank"}
						to={`/${organization.id}/products/${item.product.id}/edit`}
					>
						{item.product.name}
					</Link>
				),
				renderSummary: () => t("lib:common.word.summary"),
				renderExport: (item: StocktakingProductStateApi) => item.product.name,
			},
			{
				id: "category",
				name: t("common.word.category"),
				renderSummary: () => "",
				render: (item: StocktakingProductStateApi) => item?.product?.category?.name,
			},
			{
				id: "sku",
				name: t("modules.stocktaking.field.sku.title"),
				type: "text",
				renderSummary: () => "",
				render: (item: StocktakingProductStateApi) => item?.product?.sku,
			},
			{
				id: "amount_initial",
				name: t("common.word.amount_initial"),
				renderSummary: () => summaryData.amount_initial,
				render: (item: StocktakingProductStateApi) => item?.amount_initial,
				exportType: "number",
			},
			{
				id: "amount_actual",
				name: t("common.word.amount_actual"),
				renderSummary: () => summaryData.amount_actual,
				render: (item: StocktakingProductStateApi) => item?.amount_actual,
				exportType: "number",
			},
			{
				id: "amount_consumption",
				name: t("common.word.diff_state"),
				render: (item: StocktakingProductStateApi) =>
					drawColumnWithDifference(item.amount_consumption, item.amount_consumption),
				renderSummary: () => summaryData.amount_consumption,
				renderExport: (item: StocktakingProductStateApi) => item.amount_consumption,
				exportType: "number",
			},
			{
				id: "price_initial_net",
				name: t("common.word.initial_price_money"),
				renderSummary: () => FormatMoney(summaryData.price_initial_net),
				render: (item: StocktakingProductStateApi) => FormatMoney(item.price_initial_net),
				exportType: "number",
			},
			{
				id: "price_actual_net",
				name: t("common.word.actual_price_money"),
				renderSummary: () => FormatMoney(summaryData.price_actual_net),
				render: (item: StocktakingProductStateApi) => FormatMoney(item.price_actual_net),
				headerNameAfter: () => (
					<InfoCircleSVG className="ms-1" title={t("modules.stocktaking.field.price_actual_net.tooltip")} />
				),
				exportType: "number",
			},
			{
				id: "price_diff_net",
				name: t("common.word.diff_price_money"),
				render: (item: StocktakingProductStateApi) =>
					drawColumnWithDifference(item.price_diff_net?.amount, FormatMoney(item.price_diff_net)),
				renderSummary: () => FormatMoney(summaryData.price_diff_net),
				renderExport: (item: StocktakingProductStateApi) => FormatMoney(item.price_diff_net),
				exportType: "number",
			},
			{
				id: "price_last_outflows_net",
				name: t("modules.stocktaking.field.price_last_outflows_net.title"),
				renderSummary: () => FormatMoney(summaryData.price_last_outflows_net),
				render: (item: StocktakingProductStateApi) => FormatMoney(item.price_last_outflows_net),
				exportType: "number",
			},
			{
				id: "unit_price_net",
				name: t("modules.stocktaking.field.unit_price_net.title"),
				render: (item: StocktakingProductStateApi) => {
					const price =
						item.amount_actual === 0 ? 0 : (item.price_actual_net.amount / item.amount_actual).toFixed(2);
					const currency = item.price_actual_net.currency;
					return FormatMoney({ amount: Number(price), currency: currency || "" });
				},
				renderSummary: () => "-",
				exportType: "number",
			},
			{
				id: "sold_amount",
				name: t("modules.stocktaking.field.sold_amount.title"),
				render: (item: StocktakingProductStateApi) => item.sold_amount,
				renderSummary: () => summaryData.sold_amount,
				exportType: "number",
			},
			{
				id: "last_price",
				name: t("common.word.last_price"),
				render: (item: StocktakingProductStateApi) => FormatMoney(item.last_price),
				renderSummary: () => "-",
				exportType: "number",
			},
			{
				id: "sold_diff_percent",
				name: t("modules.stocktaking.field.sold_diff_percent.title"),
				render: (item: StocktakingProductStateApi) =>
					drawColumnWithDifference(item.sold_diff_percent, `${item.sold_diff_percent}%`),
				renderExport: (item: StocktakingProductStateApi) => `${item.sold_diff_percent}%`,
				renderSummary: () => `${summaryData.sold_diff_percent}%`,
			},
		],
		filters: [
			{
				id: "productIds",
				name: t("common.word.product"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductSearchSelect(search, params, { cancelToken: options?.token }),
				},
			},
			{
				id: "categoryIds",
				name: t("common.word.category"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductCategorySearchSelect(search, params, { cancelToken: options?.token }),
				},
			},
		],
		selectedColumns: [
			"product",
			"category",
			"amount_initial",
			"amount_actual",
			"amount_consumption",
			"price_initial_net",
			"price_actual_net",
			"price_diff_net",
			"price_last_outflows_net",
			"unit_price_net",
			"sold_amount",
			"sold_diff_percent",
			"last_price",
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: `${t("modules.stocktaking.field.summary_export_config.title")} ${stocktaking.document_number}`,
			filename: `${t("modules.stocktaking.field.summary_export_config.title")} ${stocktaking.document_number}`,
			organization: organization.name,
			skipCSVSummaryRow: true,
		},
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "category,product";
			const res = await apiOrganization.getStocktakingStates(stocktaking.id, params, {
				cancelToken: sourceToken?.token,
			});
			setSummaryData(res.summary);
			return res.data;
		},
		saveSegment: async (segment: SegmentType) => segmentListService.save(listName, resourceType, segment),
		customPageSizes: ["20", "100", "1000"],
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};

const List: FC<ListState> = ({ resource, stocktaking, onFetchSummary, summary }) => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const { addFlash } = useFlash();
	const [responseData, setResponseData] = useState<StocktakingProductStateApi[]>([]);
	const segmentContextValue = useContext(SegmentContext);
	const mobileActions = useContext(StocktakingMobileActionsContext);
	const [loading, setLoading] = useState<boolean>(false);
	const [summaryResponseData, setSummaryResponseData] = useState<SummaryApi>({} as SummaryApi);

	if (!resource) return null;

	const data = resource.read();
	const config = buildConfig(
		t,
		data,
		segmentContextValue,
		stocktaking,
		organization,
		summaryResponseData,
		setSummaryResponseData
	);

	const handleUpdateList = async () => {
		setLoading(true);
		try {
			const res = await apiOrganization.updateStocktakingStates(stocktaking.id, { include: "category,product" });
			await setResponseData(res);
			await onFetchSummary();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const customActions = () => (
		<ButtonLoading loading={loading} onClick={handleUpdateList}>
			{t("common.action.update")}
		</ButtonLoading>
	);

	const emptyListActions: EmptyListConfigAction[] = [];
	if (stocktaking.stocktaking_status !== "CLOSED") {
		emptyListActions.push({
			name: t("common.action.update"),
			click: handleUpdateList,
			variant: "primary",
		});
	}

	const newMobileActions = [
		...mobileActions,
		{
			title: t("modules.stocktaking.action.update.title"),
			action: handleUpdateList,
		},
	];

	return (
		<ListData
			data={responseData}
			config={config}
			customActions={stocktaking.stocktaking_status !== "CLOSED" ? customActions() : undefined}
			onFetch={setResponseData}
			emptyList={{ actions: emptyListActions }}
			mobileActions={newMobileActions}
			underFiltersChildren={<StocktakingSummary summary={summary!} />}
		/>
	);
};

const StocktakingSummaryTab = () => {
	const [summary, setSummary] = useState<StocktakingSummaryApi | undefined>(undefined);
	const { addFlash } = useFlash();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const { stocktaking } = useContext(StocktakingContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation();

	useEffect(() => {
		onFetchSummary();
	}, []);

	useEffect(() => {
		handleChangeTabTitle(
			`${stocktaking.document_number} | ${t("modules.stocktaking.header.title")} | ${t(
				"lib:common.word.summary"
			)}`
		);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	const onFetchSummary = async () => {
		try {
			const res = await apiOrganization.getStocktakingSummary(stocktaking.id);
			setSummary(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!summary) return <LoadingContainer />;

	return (
		<>
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} stocktaking={stocktaking} onFetchSummary={onFetchSummary} summary={summary} />
			</Suspense>
		</>
	);
};

export default StocktakingSummaryTab;
